import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Activity } from '../types/Activity';
import { Lead } from '../types/Lead';
import { User } from '../types/User';
import api from '../services/api';

// Deal interface
interface Deal {
  id: number;
  name: string;
  lead: number | { id: number; name: string };
  status: string;
}

interface ActivityFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  activity?: Activity;
  leads: Lead[];
  deals?: Deal[];
  users: User[];
  isSubmitting?: boolean;
  defaultDate?: Date;
  currentUserId?: number;
  defaultDealId?: number;
  defaultLeadId?: number;
}

const ActivityForm: React.FC<ActivityFormProps> = ({
  open,
  onClose,
  onSave,
  activity,
  leads: _leads,
  deals = [],
  users: initialUsers,
  isSubmitting = false,
  defaultDate,
  currentUserId,
  defaultDealId,
  defaultLeadId
}) => {
  // Auto-load users if not provided - inicializa com fallback imediatamente
  const defaultUser = {
    id: currentUserId || 1,
    email: 'usuario@flipr.com',
    get_full_name: currentUserId ? 'Usuário Atual' : 'Administrador'
  };
  
  // Estado para formData - inicializa com assigned_to válido
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    activity_type: 'task',
    priority: 'medium',
    status: 'scheduled',
    start_date: new Date(),
    start_time: '',
    due_date: null as Date | null,
    lead: '',
    deal: '',
    assigned_to: String(currentUserId || 1), // Sempre inicia com um ID válido
    color: '#1976d2',
    notes: ''
  });

  // SEMPRE tem pelo menos um usuário - defaultUser
  // Se initialUsers tem dados, usa; senão, usa defaultUser
  const users = (initialUsers && initialUsers.length > 0) 
    ? initialUsers 
    : [{
        id: currentUserId || 1,
        email: 'usuario@flipr.com',
        get_full_name: currentUserId ? 'Usuário Atual' : 'Administrador'
      }];

  const [errors, setErrors] = useState<Record<string, string>>({});

  const activityTypes = [
    { value: 'call', label: 'Ligar' },
    { value: 'email', label: 'Email' },
    { value: 'meeting', label: 'Reunião' },
    { value: 'task', label: 'Tarefa' },
    { value: 'lunch', label: 'Almoço' },
    { value: 'visit', label: 'Visita' },
    { value: 'whatsapp', label: 'WhatsApp' },
  ];

  const priorities = [
    { value: 'low', label: 'Baixa' },
    { value: 'medium', label: 'Média' },
    { value: 'high', label: 'Alta' },
    { value: 'urgent', label: 'Urgente' },
  ];


  useEffect(() => {
    if (activity) {
      const startDate = activity.scheduled_at ? new Date(activity.scheduled_at) : new Date();
      const assignedToId = (typeof activity.assigned_to === 'object' ? activity.assigned_to?.id : activity.assigned_to)?.toString() || String(defaultUser.id);
      
      // Extrai lead_id corretamente (pode ser objeto ou número)
      const leadId = activity.lead 
        ? (typeof activity.lead === 'object' ? activity.lead.id : activity.lead)
        : '';
      
      // Extrai deal_id corretamente (pode ser objeto ou número)
      const dealId = activity.deal
        ? (typeof activity.deal === 'object' ? activity.deal.id : activity.deal)
        : '';
      
      setFormData({
        title: activity.title || '',
        description: activity.description || '',
        activity_type: activity.activity_type || 'task',
        priority: activity.priority || 'medium',
        status: activity.status || 'scheduled',
        start_date: startDate,
        start_time: startDate.toTimeString().slice(0, 5),
        due_date: activity.follow_up_date ? new Date(activity.follow_up_date) : null,
        lead: leadId.toString(),
        deal: dealId.toString(),
        assigned_to: assignedToId,
        color: activity.color || '#1976d2',
        notes: activity.notes || ''
      });
    } else {
      const now = defaultDate || new Date();
      const startTime = now.toTimeString().slice(0, 5);
      setFormData({
        title: '',
        description: '',
        activity_type: 'task',
        priority: 'medium',
        status: 'scheduled',
        start_date: now,
        start_time: startTime,
        due_date: null,
        lead: defaultLeadId?.toString() || '',
        deal: defaultDealId?.toString() || '',
        assigned_to: String(defaultUser.id),
        color: '#1976d2',
        notes: ''
      });
    }
  }, [activity, defaultDate, defaultDealId, defaultLeadId]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Assunto é obrigatório';
    }
    if (!formData.assigned_to) {
      newErrors.assigned_to = 'Responsável é obrigatório';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Data é obrigatória';
    }
    if (!formData.start_time) {
      newErrors.start_time = 'Horário é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Função auxiliar para converter data para ISO com validação
  const toISOStringSafe = (date: Date | null | undefined): string | null => {
    if (!date) return null;
    
    // Se for string, tenta converter para Date
    if (typeof date === 'string') {
      date = new Date(date);
    }
    
    // Verifica se é uma data válida
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.error('Data inválida:', date);
      return null;
    }
    
    try {
      return date.toISOString();
    } catch (error) {
      console.error('Erro ao converter data para ISO:', date, error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Valida se start_date é válido
    if (!formData.start_date || isNaN(new Date(formData.start_date).getTime())) {
      setErrors(prev => ({ ...prev, start_date: 'Data inválida' }));
      return;
    }

    const { start_date, due_date, start_time, ...rest } = formData;
    const dataToSubmit = {
      ...rest,
      scheduled_at: toISOStringSafe(start_date as any) || new Date().toISOString(),
      follow_up_date: toISOStringSafe(due_date as any) || null,
      lead: formData.lead || null,
      deal: formData.deal || null,
      assigned_to: formData.assigned_to || null
    };

    await onSave(dataToSubmit);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(8px)',
          }
        }
      }}
      PaperProps={{
        sx: {
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur-strong)',
          WebkitBackdropFilter: 'var(--glass-blur-strong)',
          borderRadius: 'var(--radius-2xl)',
          border: '1px solid var(--glass-border)',
          boxShadow: 'var(--shadow-2xl), var(--shadow-glass-inset)',
          maxHeight: '90vh',
          color: 'var(--text-primary)',
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        borderBottom: '1px solid var(--glass-border)',
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              width: '8px',
              height: '8px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--bg-gradient)',
              boxShadow: 'var(--shadow-primary)',
            }} />
            <Typography variant="h6" sx={{
              color: 'var(--text-primary)',
              fontWeight: 'var(--font-weight-semibold)',
            }}>
              {activity ? 'Editar Tarefa' : 'Nova Tarefa'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              background: 'var(--glass-bg-subtle)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-tertiary)',
              '&:hover': {
                background: 'var(--glass-bg)',
                borderColor: 'var(--glass-border-strong)',
                color: 'var(--text-primary)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{
          pt: 2,
          '& .MuiTextField-root, & .MuiFormControl-root': {
            '& .MuiOutlinedInput-root': {
              color: 'var(--text-primary)',
              background: 'var(--glass-bg-subtle)',
              borderRadius: 'var(--radius-lg)',
              '& fieldset': {
                borderColor: 'var(--glass-border)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--glass-border-strong)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--color-primary-500)',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'var(--text-secondary)',
              '&.Mui-focused': {
                color: 'var(--color-primary-500)',
              },
            },
            '& .MuiSelect-icon': {
              color: 'var(--text-secondary)',
            },
          },
          '& .MuiMenuItem-root': {
            color: 'var(--text-primary)',
          },
        }}>
          <Box display="grid" gap={3}>
            {/* CAMPOS OBRIGATÓRIOS */}
            
            {/* Assunto da Tarefa */}
            <TextField
              label="Assunto da Tarefa *"
              fullWidth
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              placeholder="Digite o assunto da tarefa"
              autoComplete="off"
            />

            {/* Tipo de Tarefa */}
            <FormControl fullWidth>
              <InputLabel>Tipo de Tarefa *</InputLabel>
              <Select
                label="Tipo de Tarefa *"
                value={formData.activity_type}
                onChange={(e) => handleChange('activity_type', e.target.value)}
              >
                {activityTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Data e Horário */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <TextField
                label="Data do Agendamento *"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: '2100-12-31'
                }}
                value={formData.start_date instanceof Date 
                  ? formData.start_date.toISOString().slice(0, 10) 
                  : ''}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  if (!dateValue) return;
                  
                  const newDate = new Date(dateValue + 'T00:00:00');
                  const [hours, minutes] = formData.start_time.split(':');
                  if (hours && minutes) {
                    newDate.setHours(parseInt(hours), parseInt(minutes), 0);
                  }
                  handleChange('start_date', newDate);
                  handleChange('start_time', formData.start_time);
                }}
                error={!!errors.start_date}
                helperText={errors.start_date}
              />
              <TextField
                label="Horário *"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.start_time}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  if (!timeValue) return;
                  
                  const newDate = new Date(formData.start_date);
                  const [hours, minutes] = timeValue.split(':');
                  newDate.setHours(parseInt(hours), parseInt(minutes), 0);
                  handleChange('start_time', timeValue);
                  handleChange('start_date', newDate);
                }}
                error={!!errors.start_time}
                helperText={errors.start_time}
              />
            </Box>

            {/* Negociação (opcional) */}
            <FormControl fullWidth>
              <InputLabel>Negociação (opcional)</InputLabel>
              <Select
                label="Negociação (opcional)"
                value={formData.deal}
                onChange={(e) => handleChange('deal', e.target.value)}
              >
                <MenuItem value="">Nenhuma</MenuItem>
                {deals.map(deal => (
                  <MenuItem key={deal.id} value={deal.id.toString()}>
                    {deal.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Responsável */}
            <FormControl fullWidth error={!!errors.assigned_to}>
              <InputLabel>Responsável *</InputLabel>
              <Select
                label="Responsável *"
                value={formData.assigned_to || ''}
                onChange={(e) => {
                  handleChange('assigned_to', e.target.value);
                }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.id} value={String(user.id)}>
                    {user.get_full_name || user.email}
                  </MenuItem>
                ))}
              </Select>
              {errors.assigned_to && <Typography variant="caption" color="error">{errors.assigned_to}</Typography>}
            </FormControl>

            {/* CAMPOS OPCIONAIS */}
            
            <Typography variant="subtitle2" sx={{ mt: 1, color: 'var(--text-tertiary)' }}>
              Campos Opcionais
            </Typography>

            {/* Descrição */}
            <TextField
              label="Descrição da Tarefa"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detalhes adicionais sobre a tarefa"
            />

            {/* Prioridade */}
            <FormControl fullWidth>
              <InputLabel>Prioridade</InputLabel>
              <Select
                label="Prioridade"
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                {priorities.map(priority => (
                  <MenuItem key={priority.value} value={priority.value}>
                    {priority.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          borderTop: '1px solid var(--glass-border)',
          gap: 1.5,
        }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              color: 'var(--text-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '8px 20px',
              textTransform: 'none',
              fontWeight: 'var(--font-weight-medium)',
              '&:hover': {
                background: 'var(--glass-bg)',
                borderColor: 'var(--glass-border-strong)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              background: 'var(--bg-gradient)',
              borderRadius: 'var(--radius-lg)',
              padding: '8px 24px',
              textTransform: 'none',
              fontWeight: 'var(--font-weight-semibold)',
              boxShadow: 'var(--shadow-primary)',
              '&:hover': {
                background: 'var(--bg-gradient)',
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ActivityForm;